import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export const useIsVisibleInViewport = <T extends Element>(options?: {
  /**
   * number between 0 and 1, default is 0. 1 means the element must be fully visible.
   */
  threshold?: number;
  callback?: () => void;
}) => {
  const [node, setNode] = useState<T>();
  const [isVisible, setVisible] = useState<boolean>();
  const isVisibleRef = useRef(false);

  const handleRef = useCallback(
    (el: T | null) => {
      if (el) {
        setNode(el);
      }
    },
    [setNode]
  );

  const { threshold = 0, callback } = options || {};

  useEffect(() => {
    if (node && typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setVisible(entry.isIntersecting);
          if (entry.isIntersecting && !isVisibleRef.current) {
            callback?.();
          }
        },
        {
          threshold,
        }
      );

      observer.observe(node);

      return () => observer.unobserve(node);
    }
    return () => null;
  }, [node, callback, threshold]);

  useLayoutEffect(() => {
    if (!isVisibleRef.current && isVisible) {
      isVisibleRef.current = true;
    }
  }, [isVisible]);

  return {
    handleRef,
    node,
    isVisible,
    wasVisibleOnce: isVisibleRef.current,
  };
};
